import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, SearchX, Loader2, Lock, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr } from "@/lib/api";
import { trackEvent } from "@/lib/tracking";

const EMPTY = { nome: "", cognome: "", data_nascita: "", codice_fiscale: "", email: "", telefono: "", consenso_privacy: false, consenso_marketing: false };

const Field = ({ id, label, className = "", ...props }) => (
  <div className={className}>
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <Input id={id} data-testid={`verify-${id}-input`} className="mt-1.5 h-11 bg-white" required {...props} />
  </div>
);

const Result = ({ res, onReset }) => {
  if (res.result === "found")
    return (
      <div data-testid="verify-result-found" className="text-center py-4 rise">
        <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" strokeWidth={1.5} />
        <h3 className="font-serif text-3xl text-navy mt-4">Risultato trovato</h3>
        <p className="text-slate-600 mt-3 text-sm leading-relaxed">Nell'elenco Consap risulta una posizione compatibile con i tuoi dati. Per tutelare la tua riservatezza non mostriamo importi né istituti: li comunicheremo solo dopo il conferimento del mandato.</p>
        <Button asChild data-testid="verify-proceed-mandate-btn" className="mt-6 w-full h-12 bg-navy hover:bg-navy-800 text-white rounded-full">
          <Link to={`/mandato/${res.mandate_token}`}>Procedi con il mandato <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>
    );
  const cb = res.mode === "callback";
  const Icon = cb ? Clock : SearchX;
  return (
    <div data-testid={cb ? "verify-result-callback" : "verify-result-not-found"} className="text-center py-4 rise">
      <Icon className="h-14 w-14 mx-auto text-gold-dark" strokeWidth={1.5} />
      <h3 className="font-serif text-3xl text-navy mt-4">{cb ? "Richiesta ricevuta" : "Nessun risultato al momento"}</h3>
      <p className="text-slate-600 mt-3 text-sm leading-relaxed">
        {cb
          ? `Un professionista dello studio verificherà la tua posizione e ti ricontatterà entro ${res.hours} ore lavorative. Ti abbiamo inviato una email di conferma.`
          : "Nell'elenco pubblico Consap non risultano rapporti dormienti a tuo nome con questi dati. L'elenco viene aggiornato periodicamente: se emergono novità ti ricontatteremo."}
      </p>
      <Button data-testid="verify-new-search-btn" variant="outline" className="mt-6 rounded-full" onClick={onReset}>Nuova verifica</Button>
    </div>
  );
};

export const VerifyForm = () => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: k === "codice_fiscale" ? e.target.value.toUpperCase() : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.consenso_privacy) return setErr("Per procedere è necessario accettare l'informativa privacy.");
    setLoading(true);
    try {
      const { data } = await api.post("/public/verify", f);
      trackEvent("generate_lead", { result: data.result || data.mode });
      setRes(data);
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="verifica" data-testid="verify-card" className="bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(11,25,44,0.55)] border border-slate-200 p-5 sm:p-8 relative">
      <div className="absolute -top-3 left-6 bg-gold text-navy text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full">Gratuita · 60 secondi</div>
      {res ? <Result res={res} onReset={() => { setRes(null); setF(EMPTY); }} /> : (
        <form onSubmit={submit} className="space-y-4" data-testid="verify-form">
          <div>
            <h2 className="font-serif text-2xl sm:text-3xl text-navy">Verifica la tua posizione</h2>
            <p className="text-sm text-slate-500 mt-1">Titolare o erede: bastano i dati anagrafici.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field id="nome" label="Nome" value={f.nome} onChange={set("nome")} autoComplete="given-name" />
            <Field id="cognome" label="Cognome" value={f.cognome} onChange={set("cognome")} autoComplete="family-name" />
            <Field id="data_nascita" label="Data di nascita" type="date" value={f.data_nascita} onChange={set("data_nascita")} />
            <Field id="codice_fiscale" label="Codice fiscale" value={f.codice_fiscale} onChange={set("codice_fiscale")} maxLength={16} className="[&_input]:font-mono [&_input]:tracking-wider" />
            <Field id="email" label="Email" type="email" value={f.email} onChange={set("email")} autoComplete="email" />
            <Field id="telefono" label="Telefono" type="tel" value={f.telefono} onChange={set("telefono")} autoComplete="tel" />
          </div>
          <label className="flex items-start gap-3 text-xs text-slate-600 leading-relaxed cursor-pointer">
            <Checkbox data-testid="verify-consent-privacy-checkbox" checked={f.consenso_privacy} onCheckedChange={(v) => setF({ ...f, consenso_privacy: !!v })} className="mt-0.5" />
            <span>Ho letto l'<Link to="/privacy" target="_blank" className="text-navy underline">informativa privacy</Link> e acconsento al trattamento dei miei dati per la verifica richiesta (art. 6.1.b GDPR). *</span>
          </label>
          <label className="flex items-start gap-3 text-xs text-slate-500 leading-relaxed cursor-pointer">
            <Checkbox data-testid="verify-consent-marketing-checkbox" checked={f.consenso_marketing} onCheckedChange={(v) => setF({ ...f, consenso_marketing: !!v })} className="mt-0.5" />
            <span>Acconsento a essere ricontattato in caso di aggiornamenti dell'elenco Consap (facoltativo).</span>
          </label>
          {err && <p data-testid="verify-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
          <Button data-testid="verify-submit-button" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-navy hover:bg-navy-800 text-white text-base font-semibold transition-transform active:scale-[0.99]">
            {loading ? <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Consultazione elenco Consap…</span> : "Verifica gratis ora"}
          </Button>
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500"><Lock className="h-3 w-3" /> Dati cifrati · Nessun costo · Nessun impegno</p>
        </form>
      )}
    </div>
  );
};
